/* iter-51y / iter55o / iter55u — Admin-facing help drawer.
 *
 * A slide-over from the right edge with:
 *  • search bar at the top (live keyword filter, client-side)
 *  • left rail listing every section in the current `env`
 *  • right pane rendering the active section's blocks
 *  • inline Edit mode (admin-only, persists via PUT)
 *  • auto-scrolls to the section matching `currentPanelId`
 *
 * The same component serves both admin shells; pass `env` =
 * 'pro_writer' or 'bookstore' from the parent.
 *
 * iter55o — `renderInline` / `Block` / `BlockEditor` moved to
 *          `./AdminHelpBlocks.jsx`.
 * iter55u — The read-only right pane (`HelpSectionView` +
 *          `HelpSectionList`) and the edit-mode form
 *          (`HelpSectionEditor`) moved to sibling files. This file is
 *          now the stateful COORDINATOR only.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { HelpCircle, Search, Loader2, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { HelpSectionList, HelpSectionView } from './HelpSectionView';
import { HelpSectionEditor } from './HelpSectionEditor';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const _nextLid = () =>
  `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

function AdminHelpDrawerBody({ env, currentPanelId, open }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [query, setQuery]       = useState('');
  const [activeId, setActiveId] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const listRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/admin-help`);
      setSections(r.data?.sections || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load help.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const visible = useMemo(() => {
    const lower = query.trim().toLowerCase();
    return sections
      .filter((s) => s.env === env)
      .filter((s) => {
        if (!lower) return true;
        const hay = [
          s.title, s.summary,
          ...(s.blocks || []).map((b) => b.text || (b.items || []).join(' ')),
        ].join(' ').toLowerCase();
        return hay.includes(lower);
      });
  }, [sections, env, query]);

  useEffect(() => {
    if (!open || !currentPanelId || sections.length === 0) return;
    const match = sections.find((s) => s.env === env && s.panel_id === currentPanelId);
    if (match) setActiveId(`${match.env}:${match.panel_id}`);
    else if (visible[0]) setActiveId(`${visible[0].env}:${visible[0].panel_id}`);
    // iter55f58c — `env` and `visible` are read only when panel changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentPanelId, sections]);

  useEffect(() => {
    if (!visible.length) return;
    if (!visible.some((s) => `${s.env}:${s.panel_id}` === activeId)) {
      setActiveId(`${visible[0].env}:${visible[0].panel_id}`);
    }
  }, [visible, activeId]);

  const active = sections.find((s) => `${s.env}:${s.panel_id}` === activeId);

  const beginEdit = () => {
    if (!active) return;
    const deep = JSON.parse(JSON.stringify(active));
    deep.blocks = (deep.blocks || []).map((b) => ({ ...b, _lid: _nextLid() }));
    setDraft(deep);
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setDraft(null); };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = {
        ...draft,
        blocks: (draft.blocks || []).map(({ _lid: _drop, ...rest }) => rest),
      };
      await axios.put(`${API}/admin/admin-help/${draft.env}/${draft.panel_id}`, payload);
      toast.success('Section saved.');
      setEditMode(false);
      setDraft(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border px-4 py-2.5">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help…"
            className="h-8 pl-7 text-[12.5px]"
            data-testid="admin-help-search"
            autoFocus
          />
        </div>
        <div className="mt-1.5 text-[10.5px] text-muted-foreground">
          {loading ? 'Loading…' : `${visible.length} section${visible.length === 1 ? '' : 's'} · ${env === 'pro_writer' ? 'The Loom admin' : 'Bookstore admin'}`}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <HelpSectionList
          visible={visible}
          loading={loading}
          activeId={activeId}
          onSelect={setActiveId}
          query={query}
          listRef={listRef}
        />

        <div className="min-w-0 flex-1 overflow-y-auto p-5">
          {!active ? (
            <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5" />}
              {loading ? 'Loading…' : 'Pick a section on the left.'}
            </div>
          ) : editMode && draft ? (
            <HelpSectionEditor
              draft={draft}
              setDraft={setDraft}
              saving={saving}
              onSave={save}
              onCancel={cancelEdit}
            />
          ) : (
            <HelpSectionView active={active} onBeginEdit={beginEdit} />
          )}
        </div>
      </div>
    </div>
  );
}

/** The floating Help button (icon-only) + its slide-over panel. */
export default function AdminHelpButton({ env, currentPanelId }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-[12px]"
          data-testid="admin-help-open"
          title="Admin help (searchable)"
        >
          <HelpCircle className="h-3.5 w-3.5" /> Help
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full max-w-2xl flex-col p-0 sm:max-w-2xl" data-testid="admin-help-drawer">
        <SheetHeader className="border-b border-border px-4 py-3">
          <SheetTitle className="flex items-center gap-1.5 text-[13px] font-semibold">
            <HelpCircle className="h-4 w-4 text-primary" /> Admin Help
          </SheetTitle>
        </SheetHeader>
        <AdminHelpDrawerBody env={env} currentPanelId={currentPanelId} open={open} />
      </SheetContent>
    </Sheet>
  );
}
